// pages/subscription.js
import React, { useState } from 'react';
import { SubscriptionProvider, useSubscription } from '../contexts/SubscriptionContext';
import PlansList from '../components/subscriptions/PlansList';
import SubscriptionDetails from '../components/subscriptions/SubscriptionDetails';
import SubscriptionHistory from '../components/subscriptions/SubscriptionHistory';
import SubscriptionStats from '../components/subscriptions/SubscriptionStats';
import SubscriptionCheckout from '../components/subscriptions/SubscriptionCheckout';
import PlanComparison from '../components/subscriptions/PlanComparison';
import { verifyPayment } from '../api/subscriptions'; // Adjust the import based on your project structure

// Wrap content in subscription provider
const SubscriptionPageContent = ({ initialTab = 'overview' }) => {
  const { activeSubscription, refreshAllSubscriptionData } = useSubscription();
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [plansToCompare, setPlansToCompare] = useState([]);
  const [showCheckout, setShowCheckout] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);
  // const [paymentError, setPaymentError] = useState(null);


  // Refresh data on mount
  // useEffect(() => {
  //   refreshAllSubscriptionData();
  // }, [refreshAllSubscriptionData]);

  // Handle plan selection
  const handleSelectPlan = (planId) => {
    setSelectedPlanId(planId);
    setShowCheckout(true);
  };

  // Handle comparison toggling
  const handleToggleCompare = (planId) => {
    setPlansToCompare(prev => {
      if (prev.includes(planId)) {
        return prev.filter(id => id !== planId);
      } else {
        return [...prev, planId];
      }
    });
  };

  // Handle checkout success
  const handleCheckoutSuccess = async (response, plan_id = '', coupon_code = '') => {
    try {
      console.log('response from Sub.jsx: ', response.response);
      // Verify payment on the server
      const verificationData = {
        razorpay_order_id: response.response.razorpay_order_id,
        razorpay_payment_id: response.response.razorpay_payment_id,
        razorpay_signature: response.response.razorpay_signature,
        plan_id: response.plan_id,
        coupon_code: response.coupon_code || '',
      };
      console.log('Verification data ra:', verificationData);
      const payementcompletedDetails = verifyPayment(verificationData);
      console.log('Payment verification response:', payementcompletedDetails);
      
      // Update subscription status
      await refreshAllSubscriptionData();
      
      setPaymentSuccess(true);
      // setProcessingPayment(false);
    } catch (err) {
      // setPaymentError(err.message || 'Failed to verify payment');
      // setProcessingPayment(false);
    }
    
    // setPaymentSuccess(true);
    refreshAllSubscriptionData();
    // setTimeout(() => {
    //   setShowCheckout(false);
    //   setPaymentSuccess(false);
    //   setActiveTab('overview');
    // }, 3000);
  };

  // Handle checkout cancellation
  const handleCheckoutCancel = () => {
    setShowCheckout(false);
    setSelectedPlanId(null);
  };

  // Render checkout if active
  if (showCheckout) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        {paymentSuccess ? (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md mb-6">
            <p className="font-bold">Payment Successful!</p>
            <p>Your subscription has been activated. Redirecting to your subscription dashboard...</p>
            
          </div>
        ) : (
          <SubscriptionCheckout 
            planId={selectedPlanId} 
            onSuccess={handleCheckoutSuccess} 
            onCancel={handleCheckoutCancel} 
          />
        )}
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">Subscription Management</h1>
      
      {/* Tab Navigation */}
      <div className="mb-8 border-b border-gray-200">
        <nav className="flex space-x-8">
          <button 
            className={`px-1 py-4 border-b-2 font-medium text-sm ${
              activeTab === 'overview' 
                ? 'border-blue-500 text-blue-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={`px-1 py-4 border-b-2 font-medium text-sm ${
              activeTab === 'plans' 
                ? 'border-blue-500 text-blue-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            onClick={() => setActiveTab('plans')}
          >
            Plans
          </button>
          <button 
            className={`px-1 py-4 border-b-2 font-medium text-sm ${
              activeTab === 'history' 
                ? 'border-blue-500 text-blue-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            onClick={() => setActiveTab('history')}
          >
            History
          </button>
          {plansToCompare.length > 1 && (
            <button 
              className={`px-1 py-4 border-b-2 font-medium text-sm ${
                activeTab === 'compare' 
                  ? 'border-blue-500 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('compare')}
            >
              Compare Plans ({plansToCompare.length})
            </button>
          )}
        </nav>
      </div>
      
      {/* Tab Content */}
      <div>
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {activeSubscription ? (
              <>
                <SubscriptionStats />
                <SubscriptionDetails />
              </>
            ) : (
              <div className="bg-yellow-50 border border-yellow-100 text-yellow-800 p-4 rounded-md mb-6">
                <p className="font-medium">No active subscription</p>
                <p className="mt-1">Select a plan to get started with your subscription.</p>
                <button 
                  onClick={() => setActiveTab('plans')}
                  className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Browse Plans
                </button>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'plans' && (
          <div>
            {plansToCompare.length > 0 && (
              <div className="flex justify-between items-center mb-6">
                <div>
                  <span className="font-medium">{plansToCompare.length} plans selected</span>
                </div>
                <div className="space-x-4">
                  <button 
                    onClick={() => setPlansToCompare([])}
                    className="text-gray-600 hover:text-gray-800"
                  >
                    Clear selection
                  </button>
                  <button 
                    onClick={() => setActiveTab('compare')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    disabled={plansToCompare.length < 2}
                  >
                    Compare Plans
                  </button>
                </div>
              </div>
            )}
            
            <PlansList 
              onSelectPlan={handleSelectPlan} 
              onToggleCompare={handleToggleCompare}
              selectedPlans={plansToCompare}
            />
          </div>
        )}
        
        {activeTab === 'history' && (
          <SubscriptionHistory />
        )}
        
        {activeTab === 'compare' && plansToCompare.length > 1 && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Plan Comparison</h2>
              <button 
                onClick={() => {
                  setPlansToCompare([]);
                  setActiveTab('plans');
                }}
                className="text-gray-600 hover:text-gray-800"
              >
                Clear comparison
              </button>
            </div>
            <PlanComparison planIds={plansToCompare} />
          </div>
        )}
      </div>
    </div>
  );
};

// Main page with provider wrapper
const SubscriptionPage = ({ initialTab  }) => {
  return (
    <SubscriptionProvider>
      <SubscriptionPageContent initialTab={initialTab} />
    </SubscriptionProvider>
  );
};

export default SubscriptionPage;